import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import {useNavigate} from "react-router";
import {Input} from "@/components/ui/input.tsx";
import axios from "axios";
import {Button} from "@/components/ui/button.tsx";
import {useEffect, useRef, useState} from "react";
import {Separator} from "@/components/ui/separator.tsx";
import {DocumentStates} from "@/pages/companies/columns.tsx";

interface Identifiable {
    id: string
}

interface DataTableProps<TData extends Identifiable, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[],
    updateData?: any,
    loading: boolean
    pagination?: {
        currentPage: number
        perPage: number
        total: number
    }
}

export function DataTable<TData extends Identifiable, TValue>({
                                                                   columns,
                                                                   data,
                                                                   updateData,
                                                                   loading,
                                                                   pagination: paginationInfo,
                                                               }: DataTableProps<TData, TValue>) {
    const [stateFilter, setStateFilter] = useState<any>({
        "DRAFT": false,
        "ORDER_PENDING": false,
        "ORDER_CONFIRMED": false,
        "ORDER_DELIVERED": false,
        "ARCHIVED_SUCCESSFUL": false,
        "ARCHIVED_CANCELLED": false,
    });

    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
        []
    )
    const [columnVisibility, setColumnVisibility] =
        useState<VisibilityState>({})
    const [rowSelection, setRowSelection] = useState({})
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: Math.max((paginationInfo?.currentPage ?? 1) - 1, 0),
        pageSize: paginationInfo?.perPage ?? 10,
    })
    const skipPaginationSync = useRef(true)
    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        onPaginationChange: setPagination,
        manualPagination: true,
        pageCount: paginationInfo ? Math.ceil(paginationInfo.total / paginationInfo.perPage) : undefined,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
            pagination,
        },
    })

    const navigate = useNavigate();

    useEffect(() => {
        if (!paginationInfo) {
            return
        }
        const nextPageIndex = Math.max(paginationInfo.currentPage - 1, 0)
        const nextPageSize = paginationInfo.perPage
        setPagination((prev) => {
            if (prev.pageIndex === nextPageIndex && prev.pageSize === nextPageSize) {
                return prev
            }
            return {
                pageIndex: nextPageIndex,
                pageSize: nextPageSize,
            }
        })
    }, [paginationInfo?.currentPage, paginationInfo?.perPage])

    useEffect(() => {
        if (!updateData) {
            return
        }
        if (skipPaginationSync.current) {
            skipPaginationSync.current = false
            return
        }
        updateData({
            page: pagination.pageIndex + 1,
            perPage: pagination.pageSize,
            filters: {status: stateFilter},
        })
    }, [pagination.pageIndex, pagination.pageSize, stateFilter, updateData])

    function createDocument() {
        axios.post(`/api/documents`).then(res => {
            navigate(`/documents/${res.data.id}/edit`)
        })
    }

    return (<>
            <div className={"w-full flex justify-between"}>
                <Input
                    placeholder="Dokumentumok keresése..."
                    value={(table.getColumn("company_name")?.getFilterValue() as string) ?? ""}
                    onChange={(event) =>
                        table.getColumn("company_name")?.setFilterValue(event.target.value)
                    }
                    className="max-w-sm"
                />
                <Button onClick={createDocument}>Új dokumentum</Button>
            </div>
            <div className={"flex gap-1 my-2"}>

                {Object.keys(stateFilter).map(key => <Button variant={stateFilter[key] ? 'default' : 'outline'}
                                                             onClick={() => {
                                                                 const f = {...stateFilter};
                                                                 f[key] = !f[key];
                                                                 setStateFilter(f)
                                                                 setPagination((prev) => ({
                                                                     ...prev,
                                                                     pageIndex: 0,
                                                                 }))
                                                             }} key={key}>{DocumentStates[key]}</Button>)}
            </div>

            <Separator></Separator>
            <div className="rounded-md">

                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {loading ?
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    Betöltés...
                                </TableCell>
                            </TableRow>
                        :
                            table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        className={"cursor-pointer "}
                                        onClick={() => navigate(`/documents/${row.original.id}`)}
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        Nincs a keresésnek megfelelő elem.
                                    </TableCell>
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    Előző
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    Következő
                </Button>
            </div>
        </>
    )
}
